import React from 'react'
import { Card, Skeleton } from 'antd'

interface TableSkeletonProps {
  /** Number of placeholder data rows to show. */
  rows?: number
  /** Number of placeholder columns per row. */
  columns?: number
  /** Show a filter-bar-shaped block above the table (most list pages have one). */
  showFilters?: boolean
  /** Class name for the outer table card — pass the page's own table-card
   *  class (e.g. "ua-table-card") so the skeleton inherits its real sizing. */
  cardClassName?: string
  /** Extra style merged onto the outer wrapper (e.g. marginTop to match a
   *  page's real spacing above its table). */
  style?: React.CSSProperties
}

// Generic list-page loading placeholder — a filter-bar-shaped block plus a
// table-shaped block of shimmering rows/columns. Reused across every Master
// and List page instead of each one hand-building its own skeleton layout;
// only approximates column widths (not pixel-exact per page), which is
// enough to read as "a table is about to appear here" during the load.
const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 8,
  columns = 5,
  showFilters = true,
  cardClassName,
  style,
}) => {
  const colWidth = (i: number) => (i === 0 ? 170 : 90 + ((i * 37) % 70))

  return (
    <div style={style}>
      {showFilters && (
        <Card style={{ marginBottom: 14, borderRadius: 12, border: '1px solid #E4E8F0' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Skeleton.Input active size="default" style={{ width: 260 }} />
            <Skeleton.Input active size="default" style={{ width: 140 }} />
            <Skeleton.Input active size="default" style={{ width: 140 }} />
            <Skeleton.Button active size="default" style={{ width: 90 }} />
          </div>
        </Card>
      )}
      <Card className={cardClassName} style={{ borderRadius: 12, border: '1px solid #E4E8F0' }} styles={{ body: { padding: 0 } }}>
        <div style={{ display: 'flex', gap: 24, padding: '16px 20px', borderBottom: '1px solid #F1F4F9' }}>
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton.Input key={i} active size="small" style={{ width: colWidth(i), height: 14 }} />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, r) => (
          <div
            key={r}
            style={{
              display: 'flex', gap: 24, padding: '16px 20px',
              borderBottom: r === rows - 1 ? 'none' : '1px solid #F7F8FB',
            }}
          >
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton.Input key={c} active size="small" style={{ width: colWidth(c), height: 16 }} />
            ))}
          </div>
        ))}
      </Card>
    </div>
  )
}

export default TableSkeleton
